import { computed, Ref, ref, watch } from 'vue';
import { ColDef, ICellRendererParams, ValueFormatterFunc, ValueGetterFunc } from '@ag-grid-community/core';
import { ApiQueryParams } from '@/pages/OperationsInsights/types/ApiQueryParams';
import { useLogger } from '@agyletime/vue-3-common/src/composables/logger';
import { fetchAdvancedSnapshotMetadata } from '@/pages/OperationsInsights/services/fetchAdvancedSnapshotMetadata';
import { fetchAdvancedSnapshotData } from '@/pages/OperationsInsights/services/fetchAdvancedSnapshotData';
import { AdvancedSnapshotMetadata, AdvancedSnapshotPagination, AdvancedSnapshotRow } from '@/pages/OperationsInsights/types/AdvancedSnapshot';
import DateTimeCellRenderer from '@/pages/OperationsAdministration/components/DateTimeCellRenderer.vue';
import { useActiveWsDefOperationConfigRefDataStore } from '@/stores/activeWsDefOperationConfigRefDataStore';
import { secondsToTimeRange } from '@/utils';
import { differenceInSeconds } from 'date-fns';

const defaultCol = {
    resizable: true,
    sortable: true,
    filter: true,
};

const slaColors: Record<string, string> = {
    BREACHED: 'red-salsa',
    'AT RISK': 'maize-crayola',
    'WITHIN SLA': 'green-pantone',
    MET: 'bluetiful',
};
const getSlaColors = (status: string) => slaColors[status] ?? 'black-erie';
const renderSlaStatus = (status: string, tagId: string) =>
    `<al-tag
    variant="${getSlaColors(status)}"
    type="category"
    text="${status}"
    test-id="sla-status-${tagId}"
>`;
const slaStatusValueFunc: ValueGetterFunc = (params) => params.data.slaStatus.replaceAll('_', ' ');
const slaStatusCellRenderer = (params: ICellRendererParams) => renderSlaStatus(params.value, params.data._id);
const slaStatusFilterRenderer = (params: ICellRendererParams) =>
    params.value === '(Select All)' ? params.value : renderSlaStatus(params.value, `filter-${params.value}`);

const getDurationFromStart: ValueFormatterFunc = (param) => secondsToTimeRange(param.data.startTime, param.value, 2);

const { activeWsDefOperationConfigRefDataStore } = useActiveWsDefOperationConfigRefDataStore();
const nameById = computed(() => new Map(activeWsDefOperationConfigRefDataStore.data.map((ws) => [ws.workstreamDefinitionId, ws.name])));
const workstreamNameGetter: ValueGetterFunc = (params) => nameById.value.get(params.data.workstreamDefinitionId) ?? '';

export function useExplorerData(queryParams: Ref<ApiQueryParams>, selectedStatus: Ref<string>, selectedDate: Ref<Date | undefined>) {
    const logger = useLogger('useExplorerData');

    const metadata = ref<AdvancedSnapshotMetadata | undefined>();
    const rowData = ref<AdvancedSnapshotRow[]>([]);
    const nextPage = ref<AdvancedSnapshotPagination | undefined>();
    const isLoading = ref(false);
    const error = ref<string | null>(null);

    const availableDates = computed(() =>
        [...new Set(metadata.value?.workstreams.map((w) => w.snapshotDate))].map((d) => new Date(d)).toSorted((a, b) => differenceInSeconds(a, b))
    );
    const rowCount = computed(() => metadata.value?.totals.numberOfMatchingRecords ?? 0);

    const columnDefs = computed<ColDef[]>(() =>
        [
            {
                headerName: 'Workstream',
                valueGetter: workstreamNameGetter,
                filter: 'agSetColumnFilter',
            },
            {
                field: 'workstreamIdentifier',
                headerName: 'Workstream Identifier',
                filter: 'agMultiColumnFilter',
            },
            {
                field: 'startTime',
                headerName: 'Started',
                cellRenderer: DateTimeCellRenderer,
                cellRendererParams: { format: 'datetime', useTimezone: false },
                filter: 'agDateColumnFilter',
            },
            {
                field: 'closedTime',
                headerName: 'Closed',
                cellRenderer: DateTimeCellRenderer,
                cellRendererParams: {
                    format: 'datetime',
                    useTimezone: false,
                    default: '-',
                },
                filter: 'agDateColumnFilter',
            },
            {
                field: 'waitTimeSecs',
                headerName: 'Wait Time',
                valueFormatter: getDurationFromStart,
            },
            {
                field: 'ageSecs',
                headerName: 'Age',
                valueFormatter: getDurationFromStart,
            },
            {
                field: 'totalHandleTimeSecs',
                headerName: 'Total Handle Time',
                valueFormatter: getDurationFromStart,
            },
            {
                field: 'totalResolutionTimeSecs',
                headerName: 'Total Resolution Time',
                valueFormatter: getDurationFromStart,
            },
            {
                field: 'slaStatus',
                headerName: 'SLA Status',
                valueGetter: slaStatusValueFunc,
                cellRenderer: slaStatusCellRenderer,
                filter: 'agSetColumnFilter',
                filterParams: { cellRenderer: slaStatusFilterRenderer, values: Object.keys(slaColors) },
            },
        ].map((c) => ({ ...defaultCol, ...c }))
    );

    const fetchMetadata = async () => {
        if (!queryParams.value || queryParams.value.workstreamDefinitionIds.length === 0) {
            metadata.value = undefined;
            clearSnapshotData();
            return;
        }

        isLoading.value = true;
        error.value = null;

        try {
            metadata.value = await fetchAdvancedSnapshotMetadata({
                ...queryParams.value,
                type: selectedStatus.value,
            });

            await fetchData();
        } catch (err) {
            error.value = 'Failed to fetch explorer metadata';
            logger.error('Failed to fetch explorer metadata', { error: err });
        } finally {
            isLoading.value = false;
        }
    };

    const fetchData = async () => {
        if (!queryParams.value || !selectedDate.value || !metadata.value) {
            clearSnapshotData();
            return;
        }

        isLoading.value = true;
        error.value = null;

        try {
            const data = await fetchAdvancedSnapshotData({
                ...queryParams.value,
                type: selectedStatus.value,
                snapshotDate: selectedDate.value,
            });

            rowData.value = data.workstreams;
            nextPage.value = data.pagination;
        } catch (err) {
            error.value = 'Failed to fetch explorer data';
            logger.error('Failed to fetch explorer data', { error: err });
        } finally {
            isLoading.value = false;
        }
    };

    const clearSnapshotData = () => {
        rowData.value = [];
        nextPage.value = undefined;
    };

    // Watch queryParams and selections, and fetch data when they change
    watch([queryParams, selectedStatus], fetchMetadata, { immediate: true, deep: true });
    watch(
        selectedDate,
        (newDate) => {
            if (newDate) {
                return fetchData();
            } else {
                clearSnapshotData();
            }
        },
        { immediate: true, deep: true }
    );

    watch(
        availableDates,
        (newDates) => {
            if (newDates.find((d) => d.getTime() === selectedDate.value?.getTime())) {
                return fetchData();
            } else {
                selectedDate.value = undefined;
            }
        },
        { immediate: true }
    );

    return {
        columnDefs,
        rowData,
        availableDates,
        snapshotMetadata: metadata,
        rowCount,
        isLoading,
        error,
    };
}
